import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Users, BookOpen, ShoppingBag, IndianRupee, ArrowLeft, Trash2, Plus, Edit2, Eye, RotateCcw, Key, Truck, Shield, Package, CheckCircle, Edit, MessageSquare, Check, X, Upload, ExternalLink, Image as ImageIcon, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Navbar from '../components/Navbar';
import { ASSIGNABLE_ROLES, isFounder, roleLabel } from '../lib/roles';

function refundAllowed(order) {
  const status = (order?.status || '').toLowerCase();
  if (!['cancelled', 'rejected_by_lender'].includes(status)) return false;
  if (order.refund_status === 'processed' || order.pickup_status === 'collected') return false;
  const day = String(order.rental_start_date || order.start_date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return true;
  return Date.now() <= new Date(`${day}T23:59:59+05:30`).getTime();
}

const SuperAdminDashboard = () => {
  const { user: currentUser, token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [declineOrderId, setDeclineOrderId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundingId, setRefundingId] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [userDeposits, setUserDeposits] = useState([]);
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [deductTarget, setDeductTarget] = useState(null);
  const [deductAmount, setDeductAmount] = useState('');
  const [deductReason, setDeductReason] = useState('');
  const [depositAction, setDepositAction] = useState('deduct'); // deduct | credit
  const [queueBankRefund, setQueueBankRefund] = useState(false);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [customerDetailLoading, setCustomerDetailLoading] = useState(false);
  const [accessFeatures, setAccessFeatures] = useState([]);
  const [accessRoles, setAccessRoles] = useState([]);
  const [homepageAds, setHomepageAds] = useState([]);
  const [adForm, setAdForm] = useState({ image_url: '', link_url: '', alt: '' });
  const [activeTab, setActiveTab] = useState('users');
  const [overdueJobRunning, setOverdueJobRunning] = useState(false);
  const [invFilters, setInvFilters] = useState({
    title: '',
    author: '',
    owner: '',
    category: '',
    pincode: '',
    price: '',
    stock: '',
    status: 'all',
  });
  
  // Coupon form
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showEditOrderDialog, setShowEditOrderDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetTarget, setResetTarget] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [selectedUserForPasswordReset, setSelectedUserForPasswordReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [vendorForm, setVendorForm] = useState({
    name: '',
    email: '',
    whatsapp: '',
    type: 'dunzo',
    is_active: true
  });
  const [editingOrder, setEditingOrder] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchInventory = async () => {
    try {
      const booksRes = await axios.get(`${API}/admin/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => axios.get(`${API}/books`, { params: { limit: 200, page: 1 } }));
      const booksData = Array.isArray(booksRes.data)
        ? booksRes.data
        : (booksRes.data?.books || []);
      setAllBooks(Array.isArray(booksData) ? booksData : []);
      setInventoryLoaded(true);
    } catch (error) {
      setAllBooks([]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, ordersRes, couponsRes, categoriesRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/testimonials`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setOrders(ordersRes.data);
      setCoupons(couponsRes.data);
      setCategories(categoriesRes.data);
      setTestimonials(Array.isArray(testimonialsRes.data) ? testimonialsRes.data : []);
      fetchUserDeposits();
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDeposits = async () => {
    try {
      const res = await axios.get(`${API}/admin/user-deposits`, { headers: { Authorization: `Bearer ${token}` } });
      setUserDeposits(res.data);
    } catch {}
  };

  const handleDeduct = async () => {
    if (!deductAmount || parseFloat(deductAmount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!deductReason.trim()) { toast.error('Reason is mandatory'); return; }
    const endpoint = depositAction === 'credit' ? '/admin/deposit/credit' : '/admin/deposit/deduct';
    try {
      const res = await axios.post(`${API}${endpoint}`, {
        user_id: deductTarget.user_id,
        amount: parseFloat(deductAmount),
        reason: deductReason,
        ...(depositAction === 'deduct' && queueBankRefund ? { queue_bank_refund: true } : {}),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setShowDeductModal(false);
      setDeductAmount('');
      setDeductReason('');
      setDepositAction('deduct');
      setQueueBankRefund(false);
      fetchUserDeposits();
      if (selectedCustomer?.id === deductTarget.user_id) {
        // refresh detail if open
        try {
          const detail = await axios.get(`${API}/admin/users/${deductTarget.user_id}/detail`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCustomerDetail(detail.data);
        } catch {}
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Deposit update failed'); }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    const target = users.find((u) => u.id === userId);
    const hasFounder = users.some((u) => u.role === 'founder');
    if (target?.role === 'founder' && !isFounder(currentUser?.role)) {
      toast.error('Founder role cannot be changed by other roles');
      return;
    }
    if (newRole === 'founder' && !isFounder(currentUser?.role) && hasFounder) {
      toast.error('Only a Founder can assign the Founder role');
      return;
    }
    try {
      await axios.patch(
        `${API}/admin/users/${userId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User role updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  const handleToggleUserApproval = async (userId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/admin/users/${userId}`,
        { is_approved: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    const target = users.find((u) => u.id === userId);
    if (target?.role === 'founder' && !isFounder(currentUser?.role)) {
      toast.error('Founder accounts cannot be deleted by other roles');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    
    if (!couponForm.code || !couponForm.discount_value) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/coupons`,
        {
          ...couponForm,
          discount_value: parseFloat(couponForm.discount_value)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Coupon created');
      setShowCouponDialog(false);
      setCouponForm({ code: '', discount_type: 'percentage', discount_value: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleViewCustomer = async (userId) => {
    const customer = users.find(u => u.id === userId);
    if (customer) {
      setSelectedCustomer(customer);
      setShowCustomerDialog(true);
      setCustomerDetailLoading(true);
      setCustomerDetail(null);
      try {
        const res = await axios.get(`${API}/admin/users/${userId}/detail`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomerDetail(res.data);
        if (res.data?.user) setSelectedCustomer(res.data.user);
      } catch (e) {
        // Fall back to basic profile already shown
      } finally {
        setCustomerDetailLoading(false);
      }
    }
  };

  const handleTabChange = (v) => {
    setActiveTab(v);
    if (v === 'books' && !inventoryLoaded) fetchInventory();
    if (v === 'access') fetchAccessControl();
    if (v === 'ads') fetchHomepageAds();
  };

  const handleAdFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, WebP)');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image file must be under 8MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      const img = new window.Image();
      img.onload = () => {
        const MAX_WIDTH = 1600;
        const MAX_HEIGHT = 900;
        let width = img.width;
        let height = img.height;
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          if (width / height > MAX_WIDTH / MAX_HEIGHT) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          } else {
            width = Math.round((width * MAX_HEIGHT) / height);
            height = MAX_HEIGHT;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL('image/jpeg', 0.85);
        setAdForm((prev) => ({
          ...prev,
          image_url: compressed,
          alt: prev.alt || file.name.replace(/\.[^/.]+$/, ''),
        }));
      };
      img.src = uploadEvent.target?.result;
    };
    reader.readAsDataURL(file);
  };

  const fetchHomepageAds = async () => {
    try {
      const res = await axios.get(`${API}/homepage-ads`);
      setHomepageAds(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load homepage ads');
    }
  };

  const saveHomepageAd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/homepage-ads`, adForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Advertisement added to the homepage slider');
      setAdForm({ image_url: '', link_url: '', alt: '' });
      fetchHomepageAds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add advertisement');
    }
  };

  const removeHomepageAd = async (adId) => {
    try {
      await axios.delete(`${API}/admin/homepage-ads/${adId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchHomepageAds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove advertisement');
    }
  };

  const fetchAccessControl = async () => {
    try {
      const res = await axios.get(`${API}/admin/access-control`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessFeatures(res.data.features || []);
      setAccessRoles(res.data.roles || []);
    } catch (e) {
      toast.error('Failed to load access control');
    }
  };

  const toggleAccessRole = async (feature, roleValue) => {
    const current = feature.enabled_roles || [];
    const next = current.includes(roleValue)
      ? current.filter(r => r !== roleValue)
      : [...current, roleValue];
    try {
      const res = await axios.put(
        `${API}/admin/access-control/${feature.key}`,
        { enabled_roles: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccessFeatures(prev => prev.map(f => f.key === feature.key ? { ...f, ...res.data, enabled_roles: next } : f));
      toast.success('Access updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update access');
    }
  };

  const runOverdueReminders = async () => {
    setOverdueJobRunning(true);
    try {
      const res = await axios.post(`${API}/admin/jobs/overdue-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Overdue emails: ${res.data.sent} sent, ${res.data.skipped} skipped, ${res.data.failures} failed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run overdue reminders');
    } finally {
      setOverdueJobRunning(false);
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder({...order});
    setShowEditOrderDialog(true);
  };

  const submitDecline = async (alsoRefund) => {
    const reason = declineReason.trim();
    if (!reason) {
      toast.error('Please type a reason for declining');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/orders/${declineOrderId}/lender/reject`,
        { reason, refund: alsoRefund },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Order declined');
      setDeclineOrderId(null);
      setDeclineReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline order');
    }
  };

  const submitRefund = async (orderId, amount) => {
    const reason = declineReason.trim();
    if (!reason) {
      toast.error('Please type a reason');
      return;
    }
    const pay = Number(amount);
    if (!Number.isFinite(pay) || pay < 0) {
      toast.error('Enter a refund amount, or 0 to decline the refund');
      return;
    }
    setRefundingId(orderId);
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/refund`,
        { reason, amount: pay },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Refund updated');
      setDeclineOrderId(null);
      setDeclineReason('');
      setRefundAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Refund failed');
    } finally {
      setRefundingId(null);
    }
  };

  const handleUpdateOrder = async () => {
    if (!editingOrder) return;
    
    try {
      await axios.patch(
        `${API}/orders/${editingOrder.id}`,
        { status: editingOrder.status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order updated successfully');
      setShowEditOrderDialog(false);
      setEditingOrder(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  // Pickup Management Functions
  const handleMarkReadyForPickup = async (orderId) => {
    try {
      const response = await axios.post(
        `${API}/orders/${orderId}/pickup/ready`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const notified = response.data?.customer_notified;
      toast.success(
        notified
          ? 'Order marked ready for pickup. Customer notified by SMS/email.'
          : 'Order marked ready for pickup.'
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as ready');
    }
  };

  const handleMarkCollected = async (orderId) => {
    try {
      await axios.post(
        `${API}/orders/${orderId}/pickup/collected`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order marked as collected!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as collected');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({...user});
    setShowEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    try {
      await axios.patch(
        `${API}/admin/users/${editingUser.id}`,
        { 
          name: editingUser.name,
          email: editingUser.email,
          phone: editingUser.phone,
          role: editingUser.role,
          address: editingUser.address,
          city: editingUser.city,
          state: editingUser.state,
          pincode: editingUser.pincode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User updated successfully');
      setShowEditUserDialog(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleResetValues = async () => {
    if (!adminPassword) {
      toast.error('Please enter your admin password');
      return;
    }
    if (!resetTarget) {
      toast.error('Please select what to reset');
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/reset-values`,
        { 
          target: resetTarget,
          admin_password: adminPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${resetTarget} values reset successfully`);
      setShowResetDialog(false);
      setAdminPassword('');
      setResetTarget('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset values');
    }
  };

  const handleChangeUserPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/users/${selectedUserForPasswordReset.id}/reset-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully');
      setShowPasswordResetDialog(false);
      setSelectedUserForPasswordReset(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  const handleSaveVendor = async () => {
    if (!vendorForm.name || !vendorForm.email || !vendorForm.whatsapp) {
      toast.error('Please fill all vendor details');
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/vendors`,
        vendorForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vendor saved successfully');
      setShowVendorDialog(false);
      setVendorForm({ name: '', email: '', whatsapp: '', type: 'dunzo', is_active: true });
      fetchData();
    } catch (error) {
      toast.error('Failed to save vendor');
    }
  };

  const handleDeleteBook = async (book) => {
    if (!window.confirm(`Delete "${book.title || 'this book'}"? This cannot be undone.`)) return;
    
    try {
      if (book._source === 'two_layer') {
        await axios.delete(`${API}/copies/${book.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.delete(`${API}/books/${book.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success('Book deleted');
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete book');
    }
  };

  const invMatch = (value, filter) => {
    if (!filter) return true;
    return String(value ?? '').toLowerCase().includes(String(filter).trim().toLowerCase());
  };

  const filteredInventory = allBooks.filter((book) => {
    if (!invMatch(book.title, invFilters.title)) return false;
    if (!invMatch(book.author, invFilters.author)) return false;
    if (!invMatch(book.owner_name, invFilters.owner)) return false;
    if (!invMatch(book.category, invFilters.category)) return false;
    if (!invMatch(book.pincode, invFilters.pincode)) return false;
    if (invFilters.price && !String(Math.round(book.rental_price_per_week || 0)).includes(invFilters.price.trim())) return false;
    if (invFilters.stock && !String(book.stock ?? '').includes(invFilters.stock.trim())) return false;
    if (invFilters.status === 'available' && !book.is_available) return false;
    if (invFilters.status === 'rented' && book.is_available) return false;
    return true;
  });

  const handleApproveTestimonial = async (testimonialId) => {
    try {
      await axios.patch(
        `${API}/admin/testimonials/${testimonialId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Testimonial approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve testimonial');
    }
  };

  const handleRejectTestimonial = async (testimonialId) => {
    if (!window.confirm('Reject and delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${testimonialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject testimonial');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'var(--background)'}}>
        <div className="text-xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-gray-900" data-testid="super-admin-title">
              {isFounder(currentUser?.role) ? 'Founder Dashboard' : 'Executive Leader Dashboard'}
            </h1>
            <p className="text-gray-600 mt-2">Manage your entire platform from one place</p>
          </div>
          <div className="flex space-x-2">
            {/* Vendor Management Button */}
            <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex items-center">
                  <Truck className="h-4 w-4 mr-2" />
                  Vendor Management
                </Button>
              </DialogTrigger>
              <DialogContent style={{backgroundColor: 'var(--background)', color: 'var(--foreground)'}}>
                <DialogHeader>
                  <DialogTitle style={{color: 'var(--foreground)'}}>Add Logistics Vendor</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label style={{color: 'var(--foreground)'}}>Vendor Name *</Label>
                    <Input
                      value={vendorForm.name}
                      onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                      placeholder="e.g., Dunzo, Post Office"
                      style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                    />
                  </div>
                  <div>
                    <Label style={{color: 'var(--foreground)'}}>Vendor Type *</Label>
                    <Select value={vendorForm.type} onValueChange={(val) => setVendorForm({...vendorForm, type: val})}>
                      <SelectTrigger style={{backgroundColor: 'white', color: 'var(--foreground)'}}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dunzo">Dunzo</SelectItem>
                        <SelectItem value="postoffice">Post Office</SelectItem>
                        <SelectItem value="delhivery">Delhivery</SelectItem>
                        <SelectItem value="bluedart">BlueDart</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label style={{color: 'var(--foreground)'}}>Email *</Label>
                    <Input
                      type="email"
                      value={vendorForm.email}
                      onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})}
                      placeholder="vendor@example.com"
                      style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                    />
                  </div>
                  <div>
                    <Label style={{color: 'var(--foreground)'}}>WhatsApp Number *</Label>
                    <Input
                      value={vendorForm.whatsapp}
                      onChange={(e) => setVendorForm({...vendorForm, whatsapp: e.target.value.replace(/\D/g, '')})}
                      placeholder="10-digit number"
                      maxLength={10}
                      style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Order details will be automatically sent to this vendor via email and WhatsApp when orders are placed.
                  </p>
                  <div className="flex space-x-2">
                    <Button variant="outline" className="flex-1" onClick={() => setShowVendorDialog(false)}>Cancel</Button>
                    <Button className="flex-1" style={{backgroundColor: 'var(--primary)', color: 'white'}} onClick={handleSaveVendor}>
                      Save Vendor
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Reset Button */}
            <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
              <DialogTrigger asChild>
                <Button variant="destructive" className="flex items-center">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset Values
                </Button>
              </DialogTrigger>
              <DialogContent style={{backgroundColor: 'var(--background)', color: 'var(--foreground)'}}>
                <DialogHeader>
                  <DialogTitle style={{color: 'var(--foreground)'}}>⚠️ Reset Platform Values</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
                    <strong>Warning:</strong> This action will reset selected values to defaults. This cannot be undone.
                  </div>
                  <div>
                    <Label style={{color: 'var(--foreground)'}}>What do you want to reset? *</Label>
                    <Select value={resetTarget} onValueChange={setResetTarget}>
                      <SelectTrigger style={{backgroundColor: 'white', color: 'var(--foreground)'}}>
                        <SelectValue placeholder="Select target" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="book_prices">Book Prices (set to 0)</SelectItem>
                        <SelectItem value="discounts">Discounts (set to 0%)</SelectItem>
                        <SelectItem value="coupons">All Coupons (delete all)</SelectItem>
                        <SelectItem value="revenue">Revenue Amounts (reset to 0)</SelectItem>
                        <SelectItem value="all">All Values (complete reset)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label style={{color: 'var(--foreground)'}}>Enter Admin Password to Confirm *</Label>
                    <Input
                      type="password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      placeholder="Your admin password"
                      style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" className="flex-1" onClick={() => setShowResetDialog(false)}>Cancel</Button>
                    <Button variant="destructive" className="flex-1" onClick={handleResetValues}>
                      Confirm Reset
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div 
            onClick={() => navigate('/user-revenue-management')}
            className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            style={{borderLeft: '4px solid var(--primary)'}}
            data-testid="user-revenue-card"
          >
            <h3 className="font-bold text-lg mb-2">User & Revenue</h3>
            <p className="text-sm text-gray-600">Manage users, roles, and revenue</p>
            <div className="mt-2 text-sm font-semibold" style={{color: 'var(--primary)'}}>
              Open →
            </div>
          </div>

          <div 
            onClick={() => navigate('/coupon-management')}
            className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            style={{borderLeft: '4px solid var(--accent)'}}
            data-testid="coupon-management-card"
          >
            <h3 className="font-bold text-lg mb-2">Coupon Management</h3>
            <p className="text-sm text-gray-600">Create and manage coupons</p>
            <div className="mt-2 text-sm font-semibold" style={{color: 'var(--accent)'}}>
              Open →
            </div>
          </div>

          <div 
            onClick={() => navigate('/discount-management')}
            className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            style={{borderLeft: '4px solid var(--primary)'}}
            data-testid="discount-management-card"
          >
            <h3 className="font-bold text-lg mb-2">Discount Management</h3>
            <p className="text-sm text-gray-600">Configure category discounts</p>
            <div className="mt-2 text-sm font-semibold" style={{color: 'var(--primary)'}}>
              Open →
            </div>
          </div>

          <div 
            onClick={() => navigate('/payout-management')}
            className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            style={{borderLeft: '4px solid var(--accent)'}}
            data-testid="payout-management-card"
          >
            <h3 className="font-bold text-lg mb-2">Payout Management</h3>
            <p className="text-sm text-gray-600">Manage prosumer payouts</p>
            <div className="mt-2 text-sm font-semibold" style={{color: 'var(--accent)'}}>
              Open →
            </div>
          </div>

          <div 
            onClick={() => {
              setActiveTab('ads');
              fetchHomepageAds();
              setTimeout(() => {
                document.getElementById('dashboard-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 50);
            }}
            className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            style={{borderLeft: '4px solid var(--primary)'}}
            data-testid="homepage-ads-card"
          >
            <h3 className="font-bold text-lg mb-2">Homepage Ads</h3>
            <p className="text-sm text-gray-600">Founder: add fade-in slider advertisements</p>
            <div className="mt-2 text-sm font-semibold" style={{color: 'var(--primary)'}}>
              Open tab →
            </div>
          </div>

          <div 
            onClick={() => navigate('/vendor-management')}
            className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            style={{borderLeft: '4px solid var(--primary)'}}
            data-testid="vendor-management-card"
          >
            <h3 className="font-bold text-lg mb-2">Vendor Management</h3>
            <p className="text-sm text-gray-600">Manage logistics partners</p>
            <div className="mt-2 text-sm font-semibold" style={{color: 'var(--primary)'}}>
              Open →
            </div>
          </div>

          <div 
            onClick={() => navigate('/platform-analytics')}
            className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            style={{borderLeft: '4px solid var(--primary)'}}
            data-testid="platform-analytics-card"
          >
            <h3 className="font-bold text-lg mb-2">Platform Analytics</h3>
            <p className="text-sm text-gray-600">View platform insights</p>
            <div className="mt-2 text-sm font-semibold" style={{color: 'var(--primary)'}}>
              Open →
            </div>
          </div>

          <div 
            className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            style={{borderLeft: '4px solid var(--primary)'}}
            data-testid="testimonial-management-card"
            onClick={() => {
              setActiveTab('testimonials');
              setTimeout(() => {
                document.getElementById('dashboard-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 50);
            }}
          >
            <h3 className="font-bold text-lg mb-2">Testimonial Management</h3>
            <p className="text-sm text-gray-600">Approve or reject user stories</p>
            <div className="mt-2 text-sm font-semibold" style={{color: 'var(--primary)'}}>
              {testimonials.filter(t => !t.is_approved).length} pending →
            </div>
          </div>

          <div 
            onClick={() => navigate('/admin/bulk-upload')}
            className="bg-white rounded-2xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            style={{borderLeft: '4px solid var(--accent)'}}
            data-testid="bulk-upload-management-card"
          >
            <h3 className="font-bold text-lg mb-2">Bulk Book Upload</h3>
            <p className="text-sm text-gray-600">Bulk upload books with AI enrichment</p>
            <div className="mt-2 text-sm font-semibold" style={{color: 'var(--accent)'}}>
              Open →
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Users className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Users</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="stats-total-users">{stats?.total_users || 0}</p>
            <p className="text-xs text-gray-500 mt-1">All user accounts</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <BookOpen className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Inventory</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="stats-total-books">{stats?.total_books || 0}</p>
            <p className="text-xs text-gray-500 mt-1">All books in system</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <IndianRupee className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Overall Earnings</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="stats-revenue">₹{Math.round(stats?.total_revenue || 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Platform wide revenue</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <ShoppingBag className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Orders</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="stats-total-orders">{stats?.total_orders || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Customer and admin orders</p>
          </div>
        </div>

        {/* Tabs */}
        <div id="dashboard-tabs" className="w-full">
        <Tabs value={activeTab} className="w-full" onValueChange={handleTabChange}>
          <TabsList className="mb-6" style={{backgroundColor: 'white', padding: '4px'}}>
            <TabsTrigger value="users" data-testid="users-tab" style={{color: '#A89968'}}>User Management</TabsTrigger>
            <TabsTrigger value="books" data-testid="books-tab" style={{color: '#A89968'}}>Inventory</TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab" style={{color: '#A89968'}}>Orders</TabsTrigger>
            <TabsTrigger value="catalogue" data-testid="catalogue-tab" style={{color: '#A89968'}}>Catalogue</TabsTrigger>
            <TabsTrigger value="categories" data-testid="categories-tab" style={{color: '#A89968'}}>Categories</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="discounts-tab" style={{color: '#A89968'}}>Discounts</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="coupons-tab" style={{color: '#A89968'}}>Coupons</TabsTrigger>
            <TabsTrigger value="deposits" data-testid="deposits-tab" style={{color: '#A89968'}}>Deposits</TabsTrigger>
            <TabsTrigger value="access" data-testid="access-tab" style={{color: '#A89968'}}>Access Control</TabsTrigger>
            <TabsTrigger value="ads" data-testid="ads-tab" style={{color: '#A89968'}}>Homepage Ads</TabsTrigger>
            <TabsTrigger value="testimonials" id="testimonials-tab-trigger" data-testid="testimonials-tab" style={{color: '#A89968'}}>Testimonials</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email/Phone</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Role</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users.map((user) => (
                      <tr key={user.id} data-testid={`user-row-${user.id}`}>
                        <td className="px-6 py-4 text-sm text-gray-900">{user.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{user.email || user.phone}</td>
                        <td className="px-6 py-4">
                          {user.role === 'founder' && !isFounder(currentUser?.role) ? (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                              {roleLabel(user.role)}
                            </span>
                          ) : (
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleUpdateUserRole(user.id, value)}
                            disabled={user.role === 'founder' && !isFounder(currentUser?.role)}
                          >
                            <SelectTrigger className="w-44" data-testid={`role-select-${user.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ASSIGNABLE_ROLES.map((r) => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                              ))}
                              {(isFounder(currentUser?.role) || !users.some((u) => u.role === 'founder')) && (
                                <SelectItem value="founder">Founder</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            variant={user.is_approved ? "outline" : "destructive"}
                            size="sm"
                            onClick={() => handleToggleUserApproval(user.id, user.is_approved)}
                            data-testid={`approve-button-${user.id}`}
                          >
                            {user.is_approved ? 'Approved' : 'Revoked'}
                          </Button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewCustomer(user.id)}
                              style={{color: 'var(--accent)'}}
                              title="View Details"
                              data-testid={`view-user-${user.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              style={{color: 'var(--accent)'}}
                              title="Edit User"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedUserForPasswordReset(user);
                                setShowPasswordResetDialog(true);
                              }}
                              className="text-blue-600 hover:text-blue-700"
                              title="Change Password"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-user-button-${user.id}`}
                              title="Delete User"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Books Tab */}
          <TabsContent value="books">
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="px-6 py-3 border-b flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-600">
                  Showing {filteredInventory.length} of {allBooks.length} listing{allBooks.length === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/admin/bulk-upload')}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                    Bulk Upload Books
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInvFilters({ title: '', author: '', owner: '', category: '', pincode: '', price: '', stock: '', status: 'all' })}
                  >
                    Clear filters
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Title</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Author</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Owner</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Pincode</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Price/Week</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Stock</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                    <tr className="bg-white border-b">
                      <th className="px-4 py-2">
                        <Input value={invFilters.title} onChange={(e) => setInvFilters({ ...invFilters, title: e.target.value })} placeholder="Filter title" className="h-8 text-xs" />
                      </th>
                      <th className="px-4 py-2">
                        <Input value={invFilters.author} onChange={(e) => setInvFilters({ ...invFilters, author: e.target.value })} placeholder="Filter author" className="h-8 text-xs" />
                      </th>
                      <th className="px-4 py-2">
                        <Input value={invFilters.owner} onChange={(e) => setInvFilters({ ...invFilters, owner: e.target.value })} placeholder="Filter owner" className="h-8 text-xs" />
                      </th>
                      <th className="px-4 py-2">
                        <Input value={invFilters.category} onChange={(e) => setInvFilters({ ...invFilters, category: e.target.value })} placeholder="Filter category" className="h-8 text-xs" />
                      </th>
                      <th className="px-4 py-2">
                        <Input value={invFilters.pincode} onChange={(e) => setInvFilters({ ...invFilters, pincode: e.target.value })} placeholder="Pincode" className="h-8 text-xs" />
                      </th>
                      <th className="px-4 py-2">
                        <Input value={invFilters.price} onChange={(e) => setInvFilters({ ...invFilters, price: e.target.value })} placeholder="₹" className="h-8 text-xs w-24" />
                      </th>
                      <th className="px-4 py-2">
                        <Input value={invFilters.stock} onChange={(e) => setInvFilters({ ...invFilters, stock: e.target.value })} placeholder="Stock" className="h-8 text-xs w-20" />
                      </th>
                      <th className="px-4 py-2">
                        <Select value={invFilters.status} onValueChange={(v) => setInvFilters({ ...invFilters, status: v })}>
                          <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="available">Available</SelectItem>
                            <SelectItem value="rented">Rented</SelectItem>
                          </SelectContent>
                        </Select>
                      </th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredInventory.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-6 py-10 text-center text-sm text-gray-500">No books match these filters</td>
                      </tr>
                    ) : filteredInventory.map((book) => (
                      <tr key={book.id} data-testid={`book-row-${book.id}`}>
                        <td className="px-4 py-3 text-sm text-gray-900">{book.title}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{book.author}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{book.owner_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{book.category || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{book.pincode || '—'}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-indigo-600">₹{Math.round(book.rental_price_per_week || 0)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{book.stock ?? 0}</td>
                        <td className="px-4 py-3">
                          {book.is_available ? (
                            <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                              Available
                            </span>
                          ) : (
                            <span className="bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 rounded-full">
                              Rented
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => book._source === 'two_layer' && book.book_id ? navigate(`/catalog/${book.book_id}`) : navigate(`/books/${book.id}`)}
                              className="text-indigo-600 hover:text-indigo-700"
                              title="View / Edit"
                              data-testid={`edit-book-button-${book.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteBook(book)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-book-button-${book.id}`}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Order ID</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Customer</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Items</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Delivery</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Pickup Code</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Final Amount</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Pickup Status</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {orders.map((order) => (
                      <tr key={order.id} data-testid={`order-row-${order.id}`}>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          <button 
                            onClick={() => handleViewCustomer(order.user_id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {order.id.slice(0, 8)}...
                          </button>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <button 
                            onClick={() => handleViewCustomer(order.user_id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {order.customer_name || 'N/A'}
                          </button>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">{order.items.length} item(s)</td>
                        <td className="px-4 py-4 text-sm">
                          {order.delivery_method === 'pickup' ? (
                            <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                              <Shield className="h-3 w-3 mr-1" />
                              Self Pickup
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                              <Truck className="h-3 w-3 mr-1" />
                              Delivery
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          {order.pickup_code ? (
                            <span className="font-mono font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded" data-testid={`pickup-code-admin-${order.id}`}>
                              {order.pickup_code}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold" style={{color: 'var(--primary)'}}>₹{Math.round(order.final_amount || 0)}</td>
                        <td className="px-4 py-4">
                          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'ready_for_pickup' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          {order.delivery_method === 'pickup' && order.pickup_status && !['rejected_by_lender', 'cancelled', 'payment_failed', 'pending', 'failed'].includes((order.status || '').toLowerCase()) ? (
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                              order.pickup_status === 'collected' ? 'bg-green-100 text-green-800' :
                              order.pickup_status === 'ready' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {order.pickup_status === 'collected' ? '✓ Collected' : 
                               order.pickup_status === 'ready' ? '📦 Ready' : 
                               '⏳ Pending'}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-2 min-w-[180px]">
                            {order.status === 'paid_pending_lender_approval' && (
                              declineOrderId === order.id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={declineReason}
                                    onChange={(e) => setDeclineReason(e.target.value)}
                                    placeholder="Reason for declining"
                                    className="w-full min-h-[70px] rounded border p-2 text-sm"
                                  />
                                  <Button size="sm" className="w-full bg-red-600 hover:bg-red-700 text-white" onClick={() => submitDecline(false)}>
                                    Decline
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                  onClick={() => { setDeclineOrderId(order.id); setDeclineReason(''); }}
                                >
                                  Decline
                                </Button>
                              )
                            )}
                            {(order.status || '').toLowerCase() === 'rejected_by_lender' && !['processed', 'declined'].includes(order.refund_status) && (
                              declineOrderId === `refund-${order.id}` ? (
                                <div className="space-y-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max={order.final_amount || 0}
                                    value={refundAmount}
                                    onChange={(e) => setRefundAmount(e.target.value)}
                                    className="w-full rounded border p-2 text-sm"
                                    placeholder="Refund amount, 0 for none"
                                  />
                                  <textarea
                                    value={declineReason}
                                    onChange={(e) => setDeclineReason(e.target.value)}
                                    placeholder="Reason"
                                    className="w-full min-h-[70px] rounded border p-2 text-sm"
                                  />
                                  <Button size="sm" variant="outline" className="w-full" disabled={refundingId === order.id} onClick={() => submitRefund(order.id, 0)}>
                                    Decline refund
                                  </Button>
                                  <Button size="sm" className="w-full bg-red-600 hover:bg-red-700 text-white" disabled={refundingId === order.id} onClick={() => submitRefund(order.id, refundAmount)}>
                                    {refundingId === order.id ? 'Working…' : 'Refund'}
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex flex-col gap-1">
                                  <Button size="sm" variant="outline" onClick={() => { setDeclineOrderId(`refund-${order.id}`); setDeclineReason(''); setRefundAmount('0'); }}>
                                    Decline refund
                                  </Button>
                                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { setDeclineOrderId(`refund-${order.id}`); setDeclineReason(''); setRefundAmount(String(Math.round(order.final_amount || 0))); }}>
                                    Refund
                                  </Button>
                                </div>
                              )
                            )}
                          <div className="flex flex-wrap gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewCustomer(order.user_id)}
                              className="text-blue-600 hover:text-blue-700"
                              title="View Customer"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditOrder(order)}
                              style={{color: 'var(--accent)'}}
                              title="Edit Order Status"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {/* Pickup Actions */}
                            {order.delivery_method === 'pickup' && order.pickup_status === 'pending' && order.status === 'paid' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMarkReadyForPickup(order.id)}
                                className="text-amber-600 hover:text-amber-700"
                                title="Mark Ready for Pickup"
                                data-testid={`mark-ready-${order.id}`}
                              >
                                <Package className="h-4 w-4" />
                              </Button>
                            )}
                            {order.delivery_method === 'pickup' && order.pickup_status === 'ready' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMarkCollected(order.id)}
                                className="text-green-600 hover:text-green-700"
                                title="Mark as Collected"
                                data-testid={`mark-collected-${order.id}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Catalogue Tab */}
          <TabsContent value="catalogue">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-2xl font-bold mb-4">Book Catalogue Management</h2>
              <p className="text-gray-600">Manage book categories, genres, and organization</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((cat) => (
                  <div key={cat.id} className="border rounded-lg p-4" style={{borderColor: '#C19A84'}}>
                    <div className="flex items-center space-x-3">
                      <span className="text-3xl">{cat.icon}</span>
                      <div>
                        <h4 className="font-semibold">{cat.name}</h4>
                        <p className="text-sm text-gray-600">Discount: {cat.discount_percentage}%</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Category Management</h2>
                  <p className="text-gray-600">Add, edit, or remove book categories</p>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button style={{backgroundColor: 'var(--primary)', color: 'white'}}>
                      <Plus className="h-4 w-4 mr-2" /> Add Category
                    </Button>
                  </DialogTrigger>
                  <DialogContent style={{backgroundColor: 'white'}}>
                    <DialogHeader>
                      <DialogTitle className="text-gray-800">Add New Category</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      try {
                        await axios.post(`${API}/categories`, {
                          name: formData.get('name'),
                          icon: formData.get('icon') || '📚'
                        }, { headers: { Authorization: `Bearer ${token}` }});
                        toast.success('Category added!');
                        fetchData();
                        e.target.reset();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to add category');
                      }
                    }} className="space-y-4">
                      <div>
                        <Label className="text-gray-700">Category Name</Label>
                        <Input name="name" placeholder="e.g., Romance" required className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-gray-700">Icon (emoji)</Label>
                        <Input name="icon" placeholder="e.g., 💕" defaultValue="📚" className="mt-1" />
                      </div>
                      <Button type="submit" className="w-full">Add Category</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              <div className="space-y-3">
                {categories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center space-x-4">
                      <span className="text-3xl">{cat.icon}</span>
                      <div>
                        <h3 className="font-semibold text-gray-800">{cat.name}</h3>
                        {cat.subcategories?.length > 0 && (
                          <p className="text-xs text-gray-500">Subcategories: {cat.subcategories.join(', ')}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent style={{backgroundColor: 'white'}}>
                          <DialogHeader>
                            <DialogTitle className="text-gray-800">Edit Category</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={async (e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            try {
                              await axios.put(`${API}/categories/${cat.id}`, {
                                name: formData.get('name'),
                                icon: formData.get('icon')
                              }, { headers: { Authorization: `Bearer ${token}` }});
                              toast.success('Category updated!');
                              fetchData();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to update');
                            }
                          }} className="space-y-4">
                            <div>
                              <Label className="text-gray-700">Category Name</Label>
                              <Input name="name" defaultValue={cat.name} required className="mt-1" />
                            </div>
                            <div>
                              <Label className="text-gray-700">Icon (emoji)</Label>
                              <Input name="icon" defaultValue={cat.icon} className="mt-1" />
                            </div>
                            <Button type="submit" className="w-full">Save Changes</Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={async () => {
                          if (window.confirm(`Delete category "${cat.name}"?`)) {
                            try {
                              await axios.delete(`${API}/categories/${cat.id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Category deleted!');
                              fetchData();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to delete');
                            }
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Discounts Tab */}
          <TabsContent value="discounts">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-2xl font-bold mb-4">Discount Management</h2>
              <p className="text-gray-600 mb-6">Configure discounts for categories and special promotions</p>
              <div className="space-y-4">
                {categories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between border rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{cat.icon}</span>
                      <span className="font-semibold">{cat.name}</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="font-bold" style={{color: '#A89968'}}>{cat.discount_percentage}% OFF</span>
                      <Button variant="outline" size="sm">Edit</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <div className="mb-4 flex justify-end">
              <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-coupon-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Coupon
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Coupon</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateCoupon} className="space-y-4">
                    <div>
                      <Label>Coupon Code</Label>
                      <Input
                        value={couponForm.code}
                        onChange={(e) => setCouponForm({...couponForm, code: e.target.value})}
                        placeholder="e.g., SAVE20"
                        required
                        data-testid="coupon-code-input"
                      />
                    </div>
                    <div>
                      <Label>Discount Type</Label>
                      <Select value={couponForm.discount_type} onValueChange={(value) => setCouponForm({...couponForm, discount_type: value})}>
                        <SelectTrigger data-testid="discount-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage</SelectItem>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Discount Value</Label>
                      <Input
                        type="number"
                        value={couponForm.discount_value}
                        onChange={(e) => setCouponForm({...couponForm, discount_value: e.target.value})}
                        placeholder={couponForm.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 100'}
                        required
                        data-testid="discount-value-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-coupon-button">Create Coupon</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Code</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Value</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {coupons.map((coupon) => (
                      <tr key={coupon.id} data-testid={`coupon-row-${coupon.id}`}>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">{coupon.code}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 capitalize">{coupon.discount_type}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}
                        </td>
                        <td className="px-6 py-4">
                          {coupon.is_active ? (
                            <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                              Active
                            </span>
                          ) : (
                            <span className="bg-gray-100 text-gray-800 text-xs font-semibold px-3 py-1 rounded-full">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-coupon-button-${coupon.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Deposits Tab */}
          <TabsContent value="deposits">
            <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
              <p className="text-sm text-gray-500 mb-4">
                Executive Leaders can deduct or credit user deposits. Deduct only lowers the wallet — it does not send money to a bank. Withdrawals waiting for bank payout are under Payout Management. Every action is audit-trailed.
              </p>
              <h2 className="text-2xl font-bold mb-4">Security Deposits</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b" style={{borderColor: '#C19A84'}}>
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Deposit Held</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Total Deposited</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Deducted</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {userDeposits.length === 0 ? (
                      <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">No deposits yet</td></tr>
                    ) : userDeposits.map((dep) => (
                      <tr key={dep.user_id} data-testid={`deposit-row-${dep.user_id}`}>
                        <td className="px-4 py-3 text-sm font-medium">{dep.user_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{dep.user_email}</td>
                        <td className="px-4 py-3 text-sm font-bold" style={{color: 'var(--accent)'}}>₹{dep.balance}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">₹{dep.total_deposited}</td>
                        <td className="px-4 py-3 text-sm text-red-600">₹{dep.total_deducted || 0}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => { setDepositAction('deduct'); setDeductTarget(dep); setShowDeductModal(true); }}
                            className="text-xs text-red-600 hover:text-red-800 font-semibold px-2 py-1 rounded border border-red-200 hover:bg-red-50 transition-colors mr-1"
                            data-testid={`deduct-btn-${dep.user_id}`}
                          >Deduct</button>
                          <button
                            onClick={() => { setDepositAction('credit'); setDeductTarget(dep); setShowDeductModal(true); }}
                            className="text-xs text-green-700 hover:text-green-900 font-semibold px-2 py-1 rounded border border-green-200 hover:bg-green-50 transition-colors"
                            data-testid={`credit-btn-${dep.user_id}`}
                          >Credit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Deduction Modal */}
            {showDeductModal && deductTarget && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
                  <h3 className="text-lg font-bold mb-2">
                    {depositAction === 'credit' ? 'Credit / Refund Security Deposit' : 'Deduct Security Deposit'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    User: <strong>{deductTarget.user_name}</strong> | Balance: <strong>₹{deductTarget.balance}</strong>
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Amount *</label>
                      <input
                        type="number"
                        min="1"
                        max={depositAction === 'deduct' ? deductTarget.balance : undefined}
                        value={deductAmount}
                        onChange={(e) => setDeductAmount(e.target.value)}
                        className="w-full border rounded-lg p-2"
                        placeholder="Enter amount"
                        data-testid="deduct-amount-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Reason (mandatory) *</label>
                      <textarea
                        value={deductReason}
                        onChange={(e) => setDeductReason(e.target.value)}
                        className="w-full border rounded-lg p-2 text-sm"
                        rows={3}
                        placeholder={depositAction === 'credit' ? 'e.g., Cancellation refund, goodwill credit' : 'e.g., Book damaged, Late return penalty'}
                        data-testid="deduct-reason-input"
                      />
                    </div>
                    {depositAction === 'deduct' && (
                      <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={queueBankRefund}
                          onChange={(e) => setQueueBankRefund(e.target.checked)}
                        />
                        <span>
                          Also queue this amount for bank payout (Payout Management). Without this, deduct only lowers the wallet — nothing is sent to the bank.
                        </span>
                      </label>
                    )}
                    <div className="flex gap-3">
                      <button onClick={() => { setShowDeductModal(false); setDeductAmount(''); setDeductReason(''); setDepositAction('deduct'); setQueueBankRefund(false); }}
                        className="flex-1 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
                        data-testid="deduct-cancel-btn"
                      >Cancel</button>
                      <button onClick={handleDeduct}
                        className="flex-1 py-2 rounded-lg text-white font-semibold"
                        style={{backgroundColor: depositAction === 'credit' ? '#15803d' : '#dc2626'}}
                        data-testid="deduct-confirm-btn"
                      >{depositAction === 'credit' ? 'Credit' : 'Deduct'} ₹{deductAmount || 0}</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-2xl font-bold mb-4">Active Rental Orders</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b" style={{borderColor: '#C19A84'}}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Order ID</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Customer Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Items</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Amount</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {orders.map((order) => (
                      <tr key={order.id} data-testid={`subscription-row-${order.id}`}>
                        <td className="px-6 py-4 text-sm">
                          <button 
                            onClick={() => handleViewCustomer(order.user_id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {order.id.slice(0, 8)}...
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <button 
                            onClick={() => handleViewCustomer(order.user_id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {order.customer_name || 'N/A'}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{order.items.length} item(s)</td>
                        <td className="px-6 py-4 text-sm font-semibold" style={{color: '#A89968'}}>₹{Math.round(order.final_amount || 0)}</td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{backgroundColor: '#E8E4C9', color: '#A89968'}}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewCustomer(order.user_id)}
                              className="text-blue-600 hover:text-blue-700"
                              title="View Customer"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditOrder(order)}
                              style={{color: 'var(--accent)'}}
                              title="Edit Order"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Homepage Ads Tab */}
          <TabsContent value="ads">
            <div className="bg-white rounded-2xl shadow-md p-6 space-y-6">
              <div>
                <h2 className="text-2xl font-bold" style={{color: 'var(--foreground)'}}>Homepage Advertisements</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Founder and Executive Leader can add image ads to the homepage slider (fade-in carousel).
                  Visitors who click the banner will be directed to your link or to the catalogue.
                </p>
              </div>

              <form onSubmit={saveHomepageAd} className="bg-gray-50 rounded-xl p-5 border border-gray-200 max-w-2xl space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Plus className="h-4 w-4" style={{color: 'var(--primary)'}} />
                  Add New Advertisement
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Option A: Choose Image File from Device
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAdFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200 cursor-pointer"
                    data-testid="ad-file-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Option B: Enter Image URL <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="https://... or data:image/..."
                    value={adForm.image_url}
                    onChange={(e) => setAdForm({ ...adForm, image_url: e.target.value })}
                    required
                    data-testid="ad-image-url-input"
                  />
                </div>

                {adForm.image_url && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1 font-medium">Banner Preview:</p>
                    <div className="h-36 bg-amber-50 rounded-lg border border-amber-200 overflow-hidden flex items-center justify-center p-2">
                      <img
                        src={adForm.image_url}
                        alt={adForm.alt || 'Ad preview'}
                        className="max-h-full max-w-full object-contain rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Link URL <span className="text-xs text-gray-500 font-normal">(optional — default scrolls to Catalogue)</span>
                  </label>
                  <Input
                    placeholder="e.g. #browse-books, /about, /books/id, or https://..."
                    value={adForm.link_url}
                    onChange={(e) => setAdForm({ ...adForm, link_url: e.target.value })}
                    data-testid="ad-link-url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to scroll visitors directly to available books (#browse-books).
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alt Text / Title <span className="text-xs text-gray-500 font-normal">(optional)</span>
                  </label>
                  <Input
                    placeholder="e.g. Festival Reading Special — Flat 20% Off"
                    value={adForm.alt}
                    onChange={(e) => setAdForm({ ...adForm, alt: e.target.value })}
                    data-testid="ad-alt-input"
                  />
                </div>

                <Button type="submit" style={{ backgroundColor: 'var(--primary)', color: 'white' }} data-testid="add-ad-submit-btn">
                  Add Advertisement to Slider
                </Button>
              </form>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Active Slider Advertisements ({homepageAds.length})</h3>
                  <Button variant="outline" size="sm" onClick={fetchHomepageAds}>Refresh</Button>
                </div>

                {homepageAds.length === 0 ? (
                  <div className="p-6 bg-gray-50 border border-dashed rounded-xl text-center">
                    <p className="text-sm text-gray-600 font-medium">No custom ads added yet</p>
                    <p className="text-xs text-gray-500 mt-1">
                      The homepage is currently displaying the default welcome banner and &quot;How It Works&quot; slides.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {homepageAds.map((ad, idx) => (
                      <div key={ad.id || idx} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border rounded-xl p-4 bg-white shadow-sm hover:shadow transition-shadow">
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          <div className="w-24 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border flex items-center justify-center">
                            <img
                              src={ad.image_url}
                              alt={ad.alt || 'Advertisement'}
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-gray-900 truncate">{ad.alt || 'Advertisement'}</p>
                            <p className="text-xs text-gray-500 truncate mt-0.5">
                              Link:{' '}
                              {ad.link_url ? (
                                <a
                                  href={ad.link_url.startsWith('http') || ad.link_url.startsWith('/') ? ad.link_url : `https://${ad.link_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline font-mono inline-flex items-center gap-1"
                                >
                                  {ad.link_url}
                                  <ExternalLink className="h-3 w-3 inline" />
                                </a>
                              ) : (
                                <span className="text-gray-400 italic">Scrolls to catalogue (#browse-books)</span>
                              )}
                            </p>
                            <p className="text-xs text-gray-400 truncate mt-0.5 font-mono">{ad.image_url}</p>
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => removeHomepageAd(ad.id)}
                          data-testid={`delete-ad-${ad.id}`}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="access" onFocus={fetchAccessControl}>
            <div className="bg-white rounded-2xl shadow-md p-6 space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold" style={{color: 'var(--foreground)'}}>Access Control</h2>
                  <p className="text-gray-600 text-sm">These toggles are enforced for AI search, bulk enrich, and who can deduct deposits. Open this tab to load the matrix.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={fetchAccessControl}>Refresh</Button>
                  <Button
                    onClick={runOverdueReminders}
                    disabled={overdueJobRunning}
                    style={{backgroundColor: 'var(--primary)', color: 'white'}}
                    data-testid="run-overdue-job"
                  >
                    {overdueJobRunning ? 'Sending…' : 'Send overdue emails now'}
                  </Button>
                </div>
              </div>

              {accessFeatures.length === 0 ? (
                <div className="text-center py-8">
                  <Button onClick={fetchAccessControl}>Load access matrix</Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left">Feature</th>
                        {(accessRoles.length ? accessRoles : [
                          {value: 'customer', label: 'Borrower'},
                          {value: 'admin', label: 'Prosumer'},
                          {value: 'employee', label: 'Employee'},
                          {value: 'super_admin', label: 'EL'},
                          {value: 'founder', label: 'Founder'},
                        ]).map(r => (
                          <th key={r.value} className="px-3 py-3 text-center">{r.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {accessFeatures.map((feature) => (
                        <tr key={feature.key}>
                          <td className="px-4 py-3">
                            <p className="font-medium">{feature.label || feature.key}</p>
                            <p className="text-xs text-gray-500">{feature.description}</p>
                          </td>
                          {(accessRoles.length ? accessRoles : [
                            {value: 'customer'},
                            {value: 'admin'},
                            {value: 'employee'},
                            {value: 'super_admin'},
                            {value: 'founder'},
                          ]).map(r => {
                            const on = (feature.enabled_roles || []).includes(r.value);
                            return (
                              <td key={r.value} className="px-3 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() => toggleAccessRole(feature, r.value)}
                                  className={`px-2 py-1 rounded text-xs font-semibold ${on ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}
                                  data-testid={`access-${feature.key}-${r.value}`}
                                >
                                  {on ? 'On' : 'Off'}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-2xl font-bold mb-4" style={{color: 'var(--foreground)'}}>Testimonial Management</h2>
              <p className="text-gray-600 mb-6">Review and approve user stories that will appear on the homepage</p>
              
              {/* Pending Testimonials */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" style={{color: 'var(--accent)'}} />
                  Pending Approval ({testimonials.filter(t => !t.is_approved).length})
                </h3>
                {testimonials.filter(t => !t.is_approved).length === 0 ? (
                  <p className="text-gray-500 italic">No pending testimonials to review</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {testimonials.filter(t => !t.is_approved).map((testimonial) => (
                      <div key={testimonial.id} className="border rounded-xl p-4" style={{borderColor: 'var(--accent)'}}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className="text-lg" style={{color: i < testimonial.rating ? '#FFD700' : '#D1D5DB'}}>
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(testimonial.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-700 mb-3 italic">"{testimonial.message}"</p>
                        <p className="font-semibold text-sm mb-4" style={{color: 'var(--primary)'}}>— {testimonial.name}</p>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={() => handleApproveTestimonial(testimonial.id)}
                            style={{backgroundColor: 'var(--primary)', color: 'white'}}
                            className="flex-1"
                            data-testid={`approve-testimonial-${testimonial.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRejectTestimonial(testimonial.id)}
                            className="flex-1"
                            data-testid={`reject-testimonial-${testimonial.id}`}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Approved Testimonials */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Check className="h-5 w-5 mr-2 text-green-600" />
                  Approved ({testimonials.filter(t => t.is_approved).length})
                </h3>
                {testimonials.filter(t => t.is_approved).length === 0 ? (
                  <p className="text-gray-500 italic">No approved testimonials yet</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {testimonials.filter(t => t.is_approved).map((testimonial) => (
                      <div key={testimonial.id} className="border rounded-xl p-4 bg-green-50" style={{borderColor: '#22C55E'}}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-1">
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className="text-sm" style={{color: i < testimonial.rating ? '#FFD700' : '#D1D5DB'}}>
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">Approved</span>
                        </div>
                        <p className="text-gray-700 text-sm mb-2 italic line-clamp-3">"{testimonial.message}"</p>
                        <p className="font-semibold text-sm" style={{color: 'var(--primary)'}}>— {testimonial.name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
        </div>
      </div>

      {/* Customer Details Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={(open) => {
        setShowCustomerDialog(open);
        if (!open) {
          setCustomerDetail(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{backgroundColor: 'var(--background)', color: 'var(--foreground)'}}>
          <DialogHeader>
            <DialogTitle style={{color: 'var(--foreground)'}}>User Details</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-6">
              {/* Profile Section */}
              <div className="flex items-start space-x-4">
                <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {selectedCustomer.profile_image_url ? (
                    <img src={selectedCustomer.profile_image_url} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <Users className="h-10 w-10 text-gray-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold" style={{color: 'var(--foreground)'}}>{selectedCustomer.name}</h3>
                  <p className="text-sm text-gray-500">{selectedCustomer.email}</p>
                  <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs font-semibold ${
                    selectedCustomer.role === 'founder' ? 'bg-amber-100 text-amber-800' :
                    selectedCustomer.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                    selectedCustomer.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                    selectedCustomer.role === 'employee' ? 'bg-slate-100 text-slate-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {roleLabel(selectedCustomer.role)}
                  </span>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Phone</Label>
                  <p className="font-medium" style={{color: 'var(--foreground)'}}>{selectedCustomer.phone || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">User ID</Label>
                  <p className="font-medium text-sm" style={{color: 'var(--foreground)'}}>{selectedCustomer.id?.slice(0, 12)}...</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Address</Label>
                  <p className="font-medium" style={{color: 'var(--foreground)'}}>{selectedCustomer.address || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">City</Label>
                  <p className="font-medium" style={{color: 'var(--foreground)'}}>{selectedCustomer.city || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">State</Label>
                  <p className="font-medium" style={{color: 'var(--foreground)'}}>{selectedCustomer.state || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Pincode</Label>
                  <p className="font-medium" style={{color: 'var(--foreground)'}}>{selectedCustomer.pincode || 'N/A'}</p>
                </div>
              </div>

              {/* Order Stats */}
              <div className="p-4 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <h4 className="font-semibold mb-2" style={{color: 'var(--primary)'}}>Order Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold" style={{color: 'var(--primary)'}}>
                      {customerDetail?.orders_count ?? orders.filter(o => o.user_id === selectedCustomer.id).length}
                    </p>
                    <p className="text-xs text-gray-600">Total Orders</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{color: 'var(--accent)'}}>
                      ₹{(customerDetail?.orders || orders.filter(o => o.user_id === selectedCustomer.id)).reduce((sum, o) => sum + (o.final_amount || 0), 0).toFixed(0)}
                    </p>
                    <p className="text-xs text-gray-600">Total Spent</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {customerDetail?.active_rentals ?? orders.filter(o => o.user_id === selectedCustomer.id && o.status === 'delivered').length}
                    </p>
                    <p className="text-xs text-gray-600">Active Rentals</p>
                  </div>
                </div>
              </div>

              {customerDetailLoading && (
                <p className="text-sm text-gray-500 text-center">Loading full profile…</p>
              )}

              {customerDetail && (
                <div className="space-y-4 max-h-80 overflow-y-auto">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-2" style={{color: 'var(--primary)'}}>Security Deposit</h4>
                    <p className="text-lg font-bold">₹{customerDetail.deposit?.balance ?? 0}</p>
                    <p className="text-xs text-gray-500">
                      Deposited ₹{customerDetail.deposit?.total_deposited ?? 0} · Deducted ₹{customerDetail.deposit?.total_deducted ?? 0}
                    </p>
                    {(customerDetail.deposit_transactions || []).slice(0, 5).map((t) => (
                      <p key={t.id} className="text-xs text-gray-600 mt-1">
                        {t.type}: ₹{t.amount} — {t.reason || t.created_at || t.timestamp}
                      </p>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3 mr-2"
                      onClick={() => navigate(`/transactions?user=${selectedCustomer.id}`)}
                      data-testid="customer-detail-transactions-btn"
                    >
                      View Transactions
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3 text-red-600 border-red-200 mr-2"
                      onClick={() => {
                        setDepositAction('deduct');
                        setDeductTarget({
                          user_id: selectedCustomer.id,
                          user_name: selectedCustomer.name,
                          balance: customerDetail.deposit?.balance ?? 0,
                        });
                        setShowDeductModal(true);
                      }}
                      data-testid="customer-detail-deduct-btn"
                    >
                      Deduct Deposit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3 text-green-700 border-green-200"
                      onClick={() => {
                        setDepositAction('credit');
                        setDeductTarget({
                          user_id: selectedCustomer.id,
                          user_name: selectedCustomer.name,
                          balance: customerDetail.deposit?.balance ?? 0,
                        });
                        setShowDeductModal(true);
                      }}
                      data-testid="customer-detail-credit-btn"
                    >
                      Credit / Refund
                    </Button>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-2" style={{color: 'var(--primary)'}}>
                      Books Owned ({customerDetail.books_count || 0})
                    </h4>
                    {(customerDetail.books_owned || []).slice(0, 8).map((b) => (
                      <p key={b.id} className="text-xs text-gray-700 truncate">{b.title} — ₹{b.price}</p>
                    ))}
                    {(customerDetail.books_count || 0) === 0 && (
                      <p className="text-sm text-gray-500">No books listed</p>
                    )}
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-2" style={{color: 'var(--primary)'}}>Recent Payments</h4>
                    {(customerDetail.payments || []).slice(0, 8).map((p) => (
                      <p key={`${p.order_id}-${p.payment_id}`} className="text-xs text-gray-700">
                        ₹{p.amount} · {p.status} · {String(p.created_at || '').slice(0, 10)}
                      </p>
                    ))}
                    {(customerDetail.payments || []).length === 0 && (
                      <p className="text-sm text-gray-500">No payments recorded</p>
                    )}
                  </div>
                </div>
              )}

              <Button variant="outline" className="w-full" onClick={() => setShowCustomerDialog(false)}>
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={showEditOrderDialog} onOpenChange={(open) => {
        setShowEditOrderDialog(open);
        if (!open) setEditingOrder(null);
      }}>
        <DialogContent style={{backgroundColor: 'var(--background)', color: 'var(--foreground)'}}>
          <DialogHeader>
            <DialogTitle style={{color: 'var(--foreground)'}}>Edit Order</DialogTitle>
          </DialogHeader>
          {editingOrder && (
            <div className="space-y-4">
              <div>
                <Label style={{color: 'var(--foreground)'}}>Order ID</Label>
                <p className="font-mono text-sm">{editingOrder.id}</p>
              </div>
              <div>
                <Label style={{color: 'var(--foreground)'}}>Customer</Label>
                <p>{editingOrder.customer_name || 'N/A'}</p>
              </div>
              <div>
                <Label style={{color: 'var(--foreground)'}}>Amount</Label>
                <p className="font-semibold" style={{color: 'var(--primary)'}}>₹{Math.round(editingOrder.final_amount || 0)}</p>
              </div>
              <div>
                <Label style={{color: 'var(--foreground)'}}>Status *</Label>
                <Select 
                  value={editingOrder.status} 
                  onValueChange={(value) => setEditingOrder({...editingOrder, status: value})}
                >
                  <SelectTrigger style={{backgroundColor: 'white', color: 'var(--foreground)'}}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{backgroundColor: 'white', color: 'var(--foreground)'}}>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="returned">Returned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowEditOrderDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 hover:opacity-90" 
                  style={{backgroundColor: 'var(--primary)', color: 'white'}}
                  onClick={handleUpdateOrder}
                >
                  Update Order
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={(open) => {
        setShowEditUserDialog(open);
        if (!open) setEditingUser(null);
      }}>
        <DialogContent className="max-w-lg" style={{backgroundColor: 'var(--background)', color: 'var(--foreground)'}}>
          <DialogHeader>
            <DialogTitle style={{color: 'var(--foreground)'}}>Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{color: 'var(--foreground)'}}>Name *</Label>
                  <Input
                    value={editingUser.name || ''}
                    onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                    style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                  />
                </div>
                <div>
                  <Label style={{color: 'var(--foreground)'}}>Email</Label>
                  <Input
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                    style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{color: 'var(--foreground)'}}>Phone</Label>
                  <Input
                    value={editingUser.phone || ''}
                    onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                    style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                  />
                </div>
                <div>
                  <Label style={{color: 'var(--foreground)'}}>Role</Label>
                  <Select value={editingUser.role} onValueChange={(val) => setEditingUser({...editingUser, role: val})}>
                    <SelectTrigger style={{backgroundColor: 'white', color: 'var(--foreground)'}}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSIGNABLE_ROLES.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                      {(isFounder(currentUser?.role) || !users.some((u) => u.role === 'founder')) && (
                        <SelectItem value="founder">Founder</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label style={{color: 'var(--foreground)'}}>Address</Label>
                <Input
                  value={editingUser.address || ''}
                  onChange={(e) => setEditingUser({...editingUser, address: e.target.value})}
                  style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label style={{color: 'var(--foreground)'}}>City</Label>
                  <Input
                    value={editingUser.city || ''}
                    onChange={(e) => setEditingUser({...editingUser, city: e.target.value})}
                    style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                  />
                </div>
                <div>
                  <Label style={{color: 'var(--foreground)'}}>State</Label>
                  <Input
                    value={editingUser.state || ''}
                    onChange={(e) => setEditingUser({...editingUser, state: e.target.value})}
                    style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                  />
                </div>
                <div>
                  <Label style={{color: 'var(--foreground)'}}>Pincode</Label>
                  <Input
                    value={editingUser.pincode || ''}
                    onChange={(e) => setEditingUser({...editingUser, pincode: e.target.value})}
                    style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowEditUserDialog(false)}>Cancel</Button>
                <Button className="flex-1" style={{backgroundColor: 'var(--primary)', color: 'white'}} onClick={handleUpdateUser}>
                  Update User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordResetDialog} onOpenChange={(open) => {
        setShowPasswordResetDialog(open);
        if (!open) {
          setSelectedUserForPasswordReset(null);
          setNewPassword('');
        }
      }}>
        <DialogContent style={{backgroundColor: 'var(--background)', color: 'var(--foreground)'}}>
          <DialogHeader>
            <DialogTitle style={{color: 'var(--foreground)'}}>Change User Password</DialogTitle>
          </DialogHeader>
          {selectedUserForPasswordReset && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm"><strong>User:</strong> {selectedUserForPasswordReset.name}</p>
                <p className="text-sm text-gray-600">{selectedUserForPasswordReset.email}</p>
              </div>
              <div>
                <Label style={{color: 'var(--foreground)'}}>New Password *</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  style={{backgroundColor: 'white', color: 'var(--foreground)'}}
                />
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowPasswordResetDialog(false)}>Cancel</Button>
                <Button className="flex-1" style={{backgroundColor: 'var(--primary)', color: 'white'}} onClick={handleChangeUserPassword}>
                  Change Password
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
